import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { isPast } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Select,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaTrash, FaExternalLinkAlt } from 'react-icons/fa';

import { Provider } from '../../Chakra';

import useCurrentUser from '../../Hooks/userCurrentUser';
import {
  MANAGE_MY_COMPETITIONS,
  FETCH_EVENT_HEADER,
} from '../../GraphQL/Queries';
import {
  DELETE_COMPETITION_SUBMISSION,
  UPLOAD_COMPETITION_SUBMISSION,
} from '../../GraphQL/Mutations';
import useGraphQLErrorExtractor from '../../Hooks/useGraphQLErrorExtractor';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import { EVENT_TITLE } from '../../Constants/AppConstants';

const DISPLAY_DATE_FORMAT = 'MM/dd/yyyy hh:mm aa';
const USER_LONG_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const stringifyDate = (date) => format(utcToZonedTime(date, USER_LONG_TZ), DISPLAY_DATE_FORMAT);

const ExistingAttachmentRow = ({ attachment, onDelete }) => {
  const toast = useToast();
  const [refetching, setRefetching] = useState(false);
  const [removeAttachment, { loading }] = useMutation(DELETE_COMPETITION_SUBMISSION, {
    variables: { attachmentId: attachment.id },
    onCompleted: async () => {
      setRefetching(true);
      await onDelete();
      setRefetching(false);

      toast({
        title: 'Submission deleted.',
        position: 'top',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  return (
    <HStack spacing={4} width="100%">
      <a
        href={attachment.attachmentUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {attachment.attachmentName}
        <Icon as={FaExternalLinkAlt} ml={1} boxSize="0.75em" verticalAlign="baseline" />
      </a>
      <Text fontStyle="italic">
        {`Uploaded on ${stringifyDate(attachment.updatedAt)}`}
      </Text>
      <Box flex="1" />
      <Box>
        {loading || refetching ? (
          <Spinner />
        ) : (
          <IconButton
            onClick={removeAttachment}
            aria-label="Remove Attachment"
            icon={<FaTrash />}
            variant="ghost"
            colorScheme="red"
            size="sm"
          />
        )}
      </Box>
    </HStack>
  );
};
ExistingAttachmentRow.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    attachmentName: PropTypes.string.isRequired,
    attachmentUrl: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

const AddAttachmentForm = ({ memberId, onUpload }) => {
  const toast = useToast();
  const extractError = useGraphQLErrorExtractor();

  const [isFile, setIsFile] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [attachmentName, setAttachmentName] = useState('');
  const [link, setLink] = useState('');
  const [file, setFile] = useState(null);
  const [fileErrorMsg, setFileErrorMsg] = useState('');

  const [uploadCompetitionSubmission, { loading }] = useMutation(
    UPLOAD_COMPETITION_SUBMISSION,
    {
      onCompleted: async () => {
        setLink('');
        setFile(null);

        setIsRefetching(true);
        await onUpload();
        setIsRefetching(false);

        toast({
          title: 'Submission uploaded.',
          position: 'top',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (err) => {
        toast({
          title: 'Error uploading submission.',
          description: extractError(err),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const changeFile = (evt) => {
    const selectedFile = evt.target.files[0];
    const filesize = ((selectedFile.size / 1024) / 1024).toFixed(4); // MB
    if (filesize > 20) {
      setFileErrorMsg('File size too large! Please upload a file smaller than 20MB');
      return;
    }
    setFile(selectedFile);
    setFileErrorMsg('');
  };

  const changeType = (type) => {
    setIsFile(type);
    setLink('');
    setFile(null);
    setFileErrorMsg('');
  };

  const handleSave = () => {
    let name = attachmentName.trim();
    if (name === '') {
      name = undefined;
    }

    if (isFile) {
      uploadCompetitionSubmission({
        variables: {
          file,
          memberId,
          name,
        },
      });
    } else {
      uploadCompetitionSubmission({
        variables: {
          externalLink: link,
          memberId,
          name,
        },
      });
    }
  };

  return (
    <VStack width="100%" mt={1}>
      <Input
        type="text"
        width="100%"
        placeholder="Enter attachment name (optional)"
        value={attachmentName}
        onChange={(e) => setAttachmentName(e.target.value)}
        disabled={loading}
      />
      <Grid gridTemplateColumns="100px auto 100px" width="100%" gap={2} marginBlock={2} alignItems="center">
        <GridItem>
          <Select onChange={(e) => changeType(e.target.value === 'file')} value={isFile ? 'file' : 'link'}>
            <option value="file">File</option>
            <option value="link">Link</option>
          </Select>
        </GridItem>
        <GridItem>
          {isFile ? (
            <Input
              type="file"
              accept="image/*,.doc,.docx,.pdf,.zip"
              onChange={(evt) => changeFile(evt)}
              disabled={loading}
            />
          ) : (
            <Input
              type="text"
              placeholder="Enter external link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              disabled={loading}
            />
          )}
        </GridItem>
        <GridItem>
          <Button
            onClick={handleSave}
            isLoading={loading || isRefetching}
            disabled={isFile ? (!file || fileErrorMsg) : link.trim() === ''}
          >
            Save Submission
          </Button>
        </GridItem>
      </Grid>
      {fileErrorMsg && <Text color="red.500">{fileErrorMsg}</Text>}
    </VStack>
  );
};
AddAttachmentForm.propTypes = {
  memberId: PropTypes.number.isRequired,
  onUpload: PropTypes.func.isRequired,
};

const AddAttachmentRow = ({ memberId, onUpload }) => {
  const [addingAttachment, setAddingAttachment] = useState(false);

  if (addingAttachment) {
    return (
      <AddAttachmentForm
        memberId={memberId}
        onUpload={async () => {
          await onUpload();
          setAddingAttachment(false);
        }}
      />
    );
  }

  return (
    <Button onClick={() => setAddingAttachment(true)}>
      Add Submission
    </Button>
  );
};
AddAttachmentRow.propTypes = {
  memberId: PropTypes.number.isRequired,
  onUpload: PropTypes.func.isRequired,
};

const SubmissionRow = ({ member, submissionsClosed, refetch }) => {
  const hasSubmission = member.fetchEventCompetition?.fetchCompetition?.onlineSubmission;

  const existingAttachments = member.fetchEventCompetitionAttachment;
  const compName = member.fetchEventCompetition.title;

  if (!hasSubmission) {
    return (
      <Box>
        <Heading size="md">{compName}</Heading>
        <Text>
          {compName}
          {' '}
          does not accept online submissions.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md">{compName}</Heading>
      {existingAttachments.length > 0 && (
        <Box mb={4}>
          <Text fontWeight="bold">Existing Submissions:</Text>
          {existingAttachments.map((attachment) => (
            <ExistingAttachmentRow
              key={attachment.id}
              attachment={attachment}
              onDelete={refetch}
            />
          ))}
        </Box>
      )}
      {!submissionsClosed && <AddAttachmentRow memberId={member.id} onUpload={refetch} />}
    </Box>
  );
};
SubmissionRow.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fetchEventCompetition: PropTypes.shape({
      title: PropTypes.string.isRequired,
      fetchCompetition: PropTypes.shape({
        onlineSubmission: PropTypes.bool.isRequired,
      }).isRequired,
    }).isRequired,
    fetchEventCompetitionAttachment: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        attachmentName: PropTypes.string.isRequired,
        attachmentUrl: PropTypes.string.isRequired,
        updatedAt: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  submissionsClosed: PropTypes.bool.isRequired,
  refetch: PropTypes.func.isRequired,
};

const CompetitionSubmissionsContent = () => {
  const { currentUser } = useCurrentUser(true);
  const { id } = useParams();
  const { data: myCompetitionsData, refetch } = useQuery(
    MANAGE_MY_COMPETITIONS,
    {
      fetchPolicy: 'network-only',
      variables: {
        eventId: parseInt(id, 10),
        userId: parseInt(currentUser?.id, 10),
      },
    },
  );

  const { data: eventData } = useQuery(
    FETCH_EVENT_HEADER,
    {
      variables: { id: parseInt(id, 10) },
      skip: !currentUser || !id,
    },
  );

  const event = eventData?.fetchEventDetail ?? {};

  useHeaderComponentsMutation({
    title: 'COMPETITION SUBMISSIONS',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const myCompetitions = myCompetitionsData?.manageMyCompetitions ?? [];
  const submissionsClosed = isPast(utcToZonedTime(event.submissionDeadline, USER_LONG_TZ));

  return (
    <>
      {submissionsClosed && <Text>Submissions are now closed.</Text>}
      <VStack spacing={3} align="stretch" width="100%">
        {myCompetitions.map((member) => (
          <SubmissionRow
            key={member.id}
            member={member}
            submissionsClosed={submissionsClosed}
            refetch={refetch}
          />
        ))}
      </VStack>
    </>
  );
};

export default () => (
  <Provider>
    <CompetitionSubmissionsContent />
  </Provider>
);
